import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, fmtPrice, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowRight, BadgeCheck, Bike, Check, Clock3, Heart, Loader2,
  MessageSquare, Plus, ShieldCheck, ShoppingBag, Star, Store,
} from "lucide-react";
import { toast } from "sonner";

const FALLBACK = "/image-placeholder.svg";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, count } = useCart();
  const [product, setProduct] = useState(null);
  const [shop, setShop] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [canReview, setCanReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [liked, setLiked] = useState(false);

  const loadReviews = async () => {
    const response = await api.get(`/products/${id}/reviews`);
    setReviews(response.data);
  };

  useEffect(() => {
    (async () => {
      try {
        const productResponse = await api.get(`/products/${id}`);
        setProduct(productResponse.data);
        const [shopResponse, reviewResponse, canReviewResponse] = await Promise.all([
          api.get(`/shops/${productResponse.data.shop_id}`),
          api.get(`/products/${id}/reviews`).catch(() => ({ data: [] })),
          api.get(`/products/${id}/can-review`).catch(() => ({ data: { can_review: false } })),
        ]);
        setShop(shopResponse.data);
        setReviews(reviewResponse.data);
        setCanReview(canReviewResponse.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const submitReview = async () => {
    setSubmitting(true);
    try {
      await api.post(`/products/${id}/reviews`, { rating, comment, order_id: canReview?.order_id || "" });
      toast.success("شكرًا لتقييمك الموثّق!");
      setComment("");
      setRating(5);
      const canReviewResponse = await api.get(`/products/${id}/can-review`);
      setCanReview(canReviewResponse.data);
      await loadReviews();
      const productResponse = await api.get(`/products/${id}`);
      setProduct(productResponse.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="grid min-h-[55vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!product) return null;

  const hasDiscount = product.discount_percent > 0;
  const finalPrice = product.price * (1 - (product.discount_percent || 0) / 100);

  return (
    <div data-testid="product-detail" className="mx-auto max-w-6xl pb-24 md:pb-5">
      <section className="grid items-start gap-7 lg:grid-cols-[minmax(0,1.05fr)_minmax(390px,.95fr)] lg:gap-12">
        <div className="relative -mx-3 -mt-5 overflow-hidden rounded-b-[34px] bg-secondary sm:mx-0 sm:mt-0 sm:rounded-[34px] lg:sticky lg:top-24">
          <img
            src={product.image_path ? fileUrl(product.image_path) : FALLBACK}
            alt={product.name}
            className="block h-auto w-full"
          />
          <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-brand/30 to-transparent" />
          <button
            onClick={() => navigate(-1)}
            data-testid="product-back"
            className="absolute right-4 top-4 hidden h-11 items-center gap-1.5 rounded-full bg-card/90 px-4 text-xs font-extrabold text-brand shadow-sm backdrop-blur-sm transition-transform hover:-translate-y-0.5 md:flex"
          >
            <ArrowRight className="h-4 w-4" /> رجوع
          </button>
          <button
            type="button"
            aria-label={liked ? "إزالة من المفضلة" : "إضافة إلى المفضلة"}
            onClick={() => setLiked((value) => !value)}
            className={`absolute left-4 top-4 grid h-11 w-11 place-items-center rounded-full shadow-sm backdrop-blur-sm transition-all ${liked ? "bg-brand text-brand-foreground" : "bg-card/90 text-brand"}`}
          >
            <Heart className={`h-5 w-5 ${liked ? "fill-current" : ""}`} />
          </button>
          {hasDiscount && (
            <span className="absolute bottom-4 right-4 rounded-full bg-primary px-3 py-1.5 text-xs font-extrabold text-primary-foreground shadow-cta">
              وفّر {product.discount_percent}%
            </span>
          )}
        </div>

        <div className="lg:pt-3">
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex rounded-full bg-secondary px-3 py-1.5 text-[11px] font-extrabold text-foreground">{product.category}</span>
            <span className={`inline-flex items-center gap-1 text-xs font-bold ${product.in_stock ? "text-success" : "text-destructive"}`}>
              <span className={`h-2 w-2 rounded-full ${product.in_stock ? "bg-success" : "bg-destructive"}`} />
              {product.in_stock ? "متوفر الآن" : "غير متوفر"}
            </span>
          </div>

          <h1 className="mt-5 text-[30px] font-extrabold leading-[1.25] tracking-[-0.035em] sm:text-4xl lg:text-[46px]">{product.name}</h1>
          <p className="mt-3 max-w-xl text-sm leading-7 text-muted-foreground sm:text-base">
            {product.description || "منتج مختار بعناية من متجر محلي موثوق، يصلك طازجًا وبأفضل جودة."}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5" dir="ltr">
                {[1, 2, 3, 4, 5].map((number) => (
                  <Star key={number} className={`h-4 w-4 ${number <= Math.round(product.rating) ? "fill-rating text-rating" : "text-brand/10"}`} />
                ))}
              </div>
              <span className="text-xs font-extrabold">{product.rating > 0 ? product.rating : "جديد"}</span>
              {product.rating_count > 0 && <span className="text-xs text-muted-foreground">({product.rating_count} تقييم)</span>}
            </div>
            {shop && (
              <button
                data-testid="product-shop-link"
                onClick={() => navigate(`/app/shop/${shop.id}`)}
                className="flex items-center gap-1.5 rounded-full bg-card px-3 py-1.5 text-xs font-bold shadow-sm transition-transform hover:-translate-y-0.5"
              >
                <Store className="h-3.5 w-3.5 text-primary" /> {shop.name}
              </button>
            )}
          </div>

          <div className="mt-7 flex items-end gap-3 border-y border-brand/10 py-6">
            <span className="font-num text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">{fmtPrice(finalPrice)}</span>
            {hasDiscount && <span className="mb-1 text-sm text-muted-foreground line-through">{fmtPrice(product.price)}</span>}
          </div>

          <div className="mt-6 grid grid-cols-3 gap-2">
            <div className="rounded-[18px] bg-muted p-3">
              <Bike className="h-5 w-5" />
              <p className="mt-2 text-[10px] font-extrabold sm:text-xs">توصيل سريع</p>
            </div>
            <div className="rounded-[18px] bg-muted p-3">
              <ShieldCheck className="h-5 w-5" />
              <p className="mt-2 text-[10px] font-extrabold sm:text-xs">شراء موثوق</p>
            </div>
            <div className="rounded-[18px] bg-muted p-3">
              <Clock3 className="h-5 w-5" />
              <p className="mt-2 text-[10px] font-extrabold sm:text-xs">تتبع مباشر</p>
            </div>
          </div>

          <Button
            data-testid="product-add-btn"
            disabled={product.in_stock === false || (shop && shop.is_open === false)}
            onClick={() => shop && addItem(shop, product)}
            className="fixed inset-x-4 bottom-[calc(env(safe-area-inset-bottom)+1rem)] z-40 mx-auto mt-6 h-14 w-[calc(100%-2rem)] max-w-xl rounded-full bg-primary px-8 text-sm font-extrabold text-primary-foreground shadow-cta hover:bg-primary/90 sm:text-base md:static md:mt-6 md:w-full md:max-w-none"
          >
            <Plus className="ms-1 h-5 w-5" />
            {product.in_stock ? "أضف إلى السلة" : "غير متوفر"}
          </Button>
          {shop && !shop.is_open && <p className="mt-2 text-center text-xs font-bold text-destructive">المتجر مغلق حاليًا</p>}
        </div>
      </section>

      <Tabs defaultValue="details" className="mt-10 lg:mt-16">
        <TabsList className="grid h-12 w-full max-w-md grid-cols-2 rounded-full bg-secondary p-1">
          <TabsTrigger value="details" data-testid="tab-details" className="rounded-full text-xs font-extrabold data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            التفاصيل
          </TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews" className="rounded-full text-xs font-extrabold data-[state=active]:bg-brand data-[state=active]:text-brand-foreground">
            التقييمات ({reviews.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-5">
          <div className="grid gap-4 rounded-[28px] bg-card p-5 shadow-card md:grid-cols-[1.35fr_.65fr] md:p-7">
            <div>
              <h3 className="text-lg font-extrabold">عن هذا المنتج</h3>
              <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-muted-foreground">
                {product.description || "لا يوجد وصف إضافي لهذا المنتج."}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-1">
              <div className="rounded-[18px] bg-muted p-4">
                <div className="text-[10px] font-bold text-muted-foreground">التصنيف</div>
                <div className="mt-1 text-sm font-extrabold">{product.category}</div>
              </div>
              <div className="rounded-[18px] bg-muted p-4">
                <div className="text-[10px] font-bold text-muted-foreground">التوفّر</div>
                <div className={`mt-1 flex items-center gap-1 text-sm font-extrabold ${product.in_stock ? "text-success" : "text-destructive"}`}>
                  {product.in_stock && <Check className="h-4 w-4" />}
                  {product.in_stock ? "متوفر" : "نفدت الكمية"}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="reviews" className="mt-5">
          <div className="mb-4 flex items-start gap-3 rounded-[22px] bg-success-soft p-4 text-xs leading-6 text-success ring-1 ring-success/15">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
            <span>كل التقييمات هنا من مشترين استلموا المنتج فعليًا، لذلك يمكنك الاعتماد عليها.</span>
          </div>

          {canReview?.can_review && (
            <div className="mb-4 rounded-[26px] bg-card p-5 shadow-card" data-testid="review-form">
              <h3 className="text-base font-extrabold">أضف تقييمك</h3>
              <div className="mb-3 mt-4 flex gap-1.5" dir="ltr">
                {[1, 2, 3, 4, 5].map((number) => (
                  <button key={number} data-testid={`pd-star-${number}`} onClick={() => setRating(number)}>
                    <Star className={`h-8 w-8 ${number <= rating ? "fill-rating text-rating" : "text-brand/10"}`} />
                  </button>
                ))}
              </div>
              <Textarea data-testid="pd-review-comment" value={comment} onChange={(event) => setComment(event.target.value)} placeholder="شاركنا رأيك بالمنتج (اختياري)" className="rounded-2xl border-0 bg-muted" />
              <Button data-testid="pd-submit-review" onClick={submitReview} disabled={submitting} className="mt-3 rounded-full">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "نشر التقييم"}
              </Button>
            </div>
          )}

          {reviews.length === 0 ? (
            <div className="rounded-[26px] bg-muted py-12 text-center text-muted-foreground">
              <MessageSquare className="mx-auto h-9 w-9 opacity-35" />
              <p className="mt-3 text-sm font-bold">لا توجد تقييمات بعد</p>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {reviews.map((review) => (
                <div key={review.id} data-testid={`pd-review-${review.id}`} className="rounded-[24px] bg-card p-5 shadow-card">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-sm font-extrabold">{review.buyer_name}</span>
                      <span className="hidden items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-[10px] text-success sm:inline-flex">
                        <BadgeCheck className="h-3.5 w-3.5" /> مشترٍ موثّق
                      </span>
                    </div>
                    <div className="flex gap-0.5" dir="ltr">
                      {[1, 2, 3, 4, 5].map((number) => <Star key={number} className={`h-3.5 w-3.5 ${number <= review.rating ? "fill-rating text-rating" : "text-brand/10"}`} />)}
                    </div>
                  </div>
                  {review.comment && <p className="mt-3 text-sm leading-6 text-muted-foreground">{review.comment}</p>}
                  <p className="mt-3 text-[10px] text-muted-foreground">{new Date(review.created_at).toLocaleDateString("ar-SY")}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {count > 0 && (
        <button
          data-testid="go-to-cart-fab"
          onClick={() => navigate("/app/checkout")}
          className="fixed bottom-[calc(env(safe-area-inset-bottom)+5rem)] left-4 right-4 z-30 mx-auto flex h-12 max-w-md items-center justify-center gap-2 rounded-full bg-card font-extrabold text-primary shadow-cta transition-transform hover:-translate-y-0.5 md:bottom-6"
        >
          <ShoppingBag className="h-[18px] w-[18px]" /> عرض السلة ({count})
        </button>
      )}
    </div>
  );
}
